/**
 * useRedDotAssignment - React Query hooks for the Org Admin "Needs Assignment"
 * queue (ADO 535): owner-less Red Dot/CTA submissions awaiting a BCBA.
 * Query keys are flat/hyphenated to match the existing red-dot hooks
 * (['red-dots', ...], ['red-dot', id], ['assignable-users', id]).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import redDotService from '../services/api/redDotService';
import { useAuthStore } from '../stores/authStore';

// Base keys (invalidation targets — prefix-matches every org's entry).
export const NEEDS_ASSIGNMENT_QUERY_KEY = ['red-dot-needs-assignment'];
export const NEEDS_ASSIGNMENT_COUNT_QUERY_KEY = ['red-dot-needs-assignment-count'];

/**
 * Rows for the Needs Assignment table. Gated server-side on action_center:full.
 * Keys are org-scoped (matching the page's `['red-dots', organizationId]`
 * convention) so an org switch / impersonation change never serves another
 * tenant's cached queue while the fresh fetch is in flight.
 */
export function useNeedsAssignmentQueue(options?: { enabled?: boolean }) {
  const organizationId = useAuthStore((s) => s.user?.organizationId);
  return useQuery({
    queryKey: [...NEEDS_ASSIGNMENT_QUERY_KEY, organizationId],
    queryFn: () => redDotService.getNeedsAssignment(),
    ...options,
  });
}

/** Badge count for the queue's tab/section header. Org-scoped like the queue. */
export function useNeedsAssignmentCount(options?: { enabled?: boolean }) {
  const organizationId = useAuthStore((s) => s.user?.organizationId);
  return useQuery({
    queryKey: [...NEEDS_ASSIGNMENT_COUNT_QUERY_KEY, organizationId],
    queryFn: () => redDotService.getNeedsAssignmentCount(),
    ...options,
  });
}

/**
 * Assign an owner to a queued (owner-less) Red Dot — reuses the existing
 * `PATCH /red-dot/:id/owner` transfer endpoint. On success invalidates the
 * queue, its count, and every red-dot list/detail cache entry so the row
 * disappears from the queue and reflects its new owner everywhere else.
 */
export function useAssignRedDotOwner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; newOwnerId: string; reason: string }) =>
      redDotService.transferRedDotOwner(vars.id, vars.newOwnerId, vars.reason),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: NEEDS_ASSIGNMENT_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: NEEDS_ASSIGNMENT_COUNT_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['red-dots'] });
      queryClient.invalidateQueries({ queryKey: ['red-dot', vars.id] });
      queryClient.invalidateQueries({ queryKey: ['assignable-users', vars.id] });
    },
  });
}
